import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Form, Input, message, Modal, Radio, Select, Table } from "antd";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import CardFooterAction from "../../../components/CardFooterAction";
import { masterDataTypeService } from "../../../services/master-data-type.service";
import { Icon } from "@iconify/react/dist/iconify.js";
import RenderIf from "../../../components/RenderIf";
import TableAction from "../../../components/TableAction";

export default function MasterDataTypeForm(){

  const [form] = Form.useForm()
  const [attributeForm] = Form.useForm()

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const { id } = useParams()

  const [attributeModal, setAttributeModal] = useState({
    open: false,
    id: null
  })

  const { data: masterDataType  } = useQuery({
    queryKey: ['masterDataType', id],
    queryFn: () => masterDataTypeService.getById(id),
    enabled: !!id
  })

  useEffect(() => {
    if(id){
      form.setFieldsValue(masterDataType?.data)
    }
  }, [masterDataType, form, id])

  const attributeType = Form.useWatch('dataType', attributeForm)

  const storeMutation = useMutation({
    mutationFn: val => masterDataTypeService.store(val),
    onSuccess: () => {
      message.success("Data Berhasil dibuat")
      navigate('/master-data/master-data-type') 
    },
    onError: (err) => {
      message.error("Terjadi Kesalahan")
      console.log(err)
    }
  })

  const storeAttributeMutation = useMutation({
    mutationFn: val => masterDataTypeService.storeAttribute(id, val),
    onSuccess: () => {
      queryClient.invalidateQueries('masterDataType')
      handleCloseAttributeModal()
      message.success("Attribute Berhasil ditambah")
    },
    onError: (err) => {
      message.error("Terjadi Kesalahan")
      console.log(err)
    }
  })

  const updateAttributeMutation = useMutation({
    mutationFn: val => masterDataTypeService.updateAttribute(val),
    onSuccess: () => {
      queryClient.invalidateQueries('masterDataType')
      handleCloseAttributeModal()
      message.success("Attribute Berhasil Diubah")
    },
    onError: (err) => {
      message.error("Terjadi Kesalahan")
      console.log(err)
    }
  })

  const deleteAttributeMutation = useMutation({
    mutationFn: val => masterDataTypeService.deleteAttribute(val),
    onSuccess: () => {
      queryClient.invalidateQueries('masterDataType')
      handleCloseAttributeModal()
      message.success("Attribute Berhasil dihapus")
    },
    onError: (err) => {
      message.error("Terjadi Kesalahan")
      console.log(err)
    }
  })

  const updateMutation = useMutation({
    mutationFn: val => masterDataTypeService.update(val),
    onSuccess: () => {
      message.success("Data Berhasil diubah")
      navigate('/master-data/master-data-type') 
    },
    onError: (err) => {
      message.error("Terjadi Kesalahan")
      console.log(err)
    }
  })

  const handleSubmit = val => {
    if(id) updateMutation.mutate(val)
    else storeMutation.mutate(val)
  }

  const handleSubmitAttribute = val => {
    if(attributeModal.id){
      const updatedValue = {
        ...val,
        id: attributeModal.id
      }
      
      if(val.dataType === 'Option' && val.options) {
        const record = masterDataType?.data?.attributes.find(e => e.id == attributeModal.id)
        const existingOptions = record?.options.map(e => {
          const changedOption = val.options.find(o => o.id == e.id)
          return { 
            id:  e.id,
            name: changedOption ? changedOption.name : e.name,
            deleted: !changedOption
          }
        })
        const newOptions = val.options.filter(opt => !opt.id)
        
        updatedValue.options = {
          existing: existingOptions,
          new: newOptions
        }
      }
      
      updateAttributeMutation.mutate({...updatedValue, id: attributeModal.id})
    } else {
      storeAttributeMutation.mutate(val)
    }
  }

  const handleCloseAttributeModal = () => {
    attributeForm.resetFields()
    setAttributeModal({ open: false, id: null })
  }

  const handleEditAttribute = (record) => {
    attributeForm.setFieldsValue(record)
    if(record.dataType == 'Option'){
      attributeForm.setFieldValue('options', record.options)
    }
    setAttributeModal({ open: true, id: record.id })
  }

  return (
    <>
      <Card
        title={id ? "Ubah Data" : "Tambah Data"}
        actions={[
          <CardFooterAction
            key={1}
            onClick={() => form.submit()}
            route={'/master-data/master-data-type'}
          />
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Form.Item hidden name={'id'} >
            <Input  />
          </Form.Item>
          <Form.Item
            label="Jenis"
            name={'name'}
            rules={[{ required: true }]}
          >
            <Input/>
          </Form.Item>
          <Form.Item  name={'description'} label="Deskripsi" >
            <Input.TextArea/>
          </Form.Item>
          <Form.Item  rules={[{ required: true }]} name={'status'} label="Status" >
            <Radio.Group>
              <Radio style={{ marginRight: 80}} value={true} >Active</Radio>
              <Radio value={false} >Inactive</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Card>
      <Card
        title={'Attribute'}
        style={{ marginTop: 20 }}
        extra={
          <Button
            variant="solid"
            color="primary"
            onClick={() => setAttributeModal({ open: true, id: null })}
          >
            Tambah
          </Button>
        }
      >
        <Table
          size="small"
          dataSource={masterDataType?.data?.attributes}
          pagination={false}
          columns={[
            {
              title: 'Attribute',
              dataIndex: 'name',
              width: 130
            },
            {
              title: 'Tipe Data',
              dataIndex: 'dataType'
            },
            {
              title: 'Aksi',
              align: 'right',
              width: 80,
              render: (record) => (
                <TableAction
                  handleDelete={() => deleteAttributeMutation.mutate(record.id)}
                  onClickEdit={() => handleEditAttribute(record)}
                />
              )
            },
          ]}
        />
      </Card>
      <Modal
        open={attributeModal.open}
        centered
        title="Attribut Master Data"
        onOk={() => attributeForm.submit()}
        onCancel={handleCloseAttributeModal}
      > 
        <Form
          form={attributeForm}
          layout="vertical"
          onFinish={handleSubmitAttribute}
        >
          <Form.Item name={'name'} label="Nama Attribut" rules={[{ required: true }]} >
            <Input/>
          </Form.Item>
          <Form.Item name={'description'} label="Deskripsi" rules={[{ required: true }]} >
            <Input.TextArea/>
          </Form.Item>
          <Form.Item name={'dataType'} label="Tipe Data" rules={[{ required: true }]} >
            <Select
              options={['Option', 'Numeric','Text'].map(e => ({ label: e, value: e }))}
            />
          </Form.Item>
          <RenderIf when={attributeType == 'Option'} >
            <Form.List
              name="options"
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <>
                      <Form.Item
                        label={index === 0 ? 'Opsi' : ''}
                        required={false}
                        key={field.key}
                      >
                        <Form.Item hidden name={[field.name, 'id']} >
                          <Input/>
                        </Form.Item>
                        <Form.Item name={[field.name, 'name']}  noStyle >
                          <Input  
                            placeholder={"Opsi " + (index+1)}  
                            addonAfter={
                              <Button
                                type="text"
                                size="small"
                                danger
                                onClick={() => remove(field.name)}
                                icon={<Icon icon={'solar:minus-circle-broken'} width={18} />}
                              />
                            }
                          />
                        </Form.Item>
                      </Form.Item>
                    </>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                    >
                      Tambah Opsi
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </RenderIf>
        </Form>
      </Modal>
    </>   
  )
}